import React, { useState, useEffect } from 'react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import Modal from './Modal';
import Button from './Button';
import Input from './Input';

interface MapPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectLocation: (coordinates: string, url: string) => void;
  initialCoordinates?: string;
}

const MapPicker: React.FC<MapPickerProps> = ({
  isOpen,
  onClose,
  onSelectLocation,
  initialCoordinates
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [manualLat, setManualLat] = useState('');
  const [manualLng, setManualLng] = useState('');
  const [selectedCoords, setSelectedCoords] = useState(initialCoordinates || '');

  useEffect(() => {
    if (initialCoordinates) {
      const [lat, lng] = initialCoordinates.split(',');
      setManualLat(lat || '');
      setManualLng(lng || '');
      setSelectedCoords(initialCoordinates);
    }
  }, [initialCoordinates]);

  const handleSearch = () => {
    if (searchQuery) {
      // Open Google Maps with search query
      const searchUrl = `https://www.google.com/maps/search/${encodeURIComponent(searchQuery)}`;
      window.open(searchUrl, '_blank');
    }
  };

  const handleManualCoordinates = () => {
    if (manualLat && manualLng) {
      const coords = `${manualLat},${manualLng}`;
      setSelectedCoords(coords);
      
      // Open in Google Maps to verify
      const mapUrl = `https://maps.google.com/?q=${manualLat},${manualLng}`;
      window.open(mapUrl, '_blank');
    }
  };

  const handleConfirmLocation = () => {
    if (selectedCoords) {
      const [lat, lng] = selectedCoords.split(',');
      const mapUrl = `https://maps.google.com/?q=${lat},${lng}`;
      onSelectLocation(selectedCoords, mapUrl);
      onClose();
    }
  };

  const openGoogleMapsForPicking = () => {
    // Open Google Maps in a new tab
    // User can right-click on any location and select "What's here?" to get coordinates
    const mapsUrl = 'https://maps.google.com';
    window.open(mapsUrl, '_blank');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Location on Map"
      size="lg"
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">How to mark location:</h4>
          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
            <li>Click "Open Google Maps" button below</li>
            <li>Find your desired location on the map</li>
            <li>Right-click on the exact spot and select "What's here?"</li>
            <li>Copy the coordinates from the bottom of the screen</li>
            <li>Paste them in the manual entry fields below</li>
          </ol>
        </div>

        {/* Open Google Maps Button */}
        <div className="text-center">
          <Button
            onClick={openGoogleMapsForPicking}
            size="lg"
            className="w-full"
          >
            <MapPinIcon className="w-5 h-5 mr-2" />
            Open Google Maps to Select Location
          </Button>
        </div>

        <div className="border-t pt-6">
          <h4 className="font-medium text-gray-900 mb-4">Search Location</h4>
          <div className="flex gap-2">
            <Input
              placeholder="Enter address or place name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} variant="outline">
              Search
            </Button>
          </div>
        </div>

        {/* Manual Coordinate Entry */}
        <div className="border-t pt-6">
          <h4 className="font-medium text-gray-900 mb-4">Manual Coordinate Entry</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Latitude
              </label>
              <Input
                type="text"
                placeholder="10.7905"
                value={manualLat}
                onChange={(e) => setManualLat(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Longitude
              </label>
              <Input
                type="text"
                placeholder="78.7047"
                value={manualLng}
                onChange={(e) => setManualLng(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <Button
              onClick={handleManualCoordinates}
              variant="outline"
              disabled={!manualLat || !manualLng}
              className="flex-1"
            >
              Verify on Map
            </Button>
            <Button
              onClick={() => {
                const coords = `${manualLat},${manualLng}`;
                setSelectedCoords(coords);
              }}
              variant="outline"
              disabled={!manualLat || !manualLng}
              className="flex-1"
            >
              Set Coordinates
            </Button>
          </div>
        </div>

        {/* Selected Coordinates Display */}
        {selectedCoords && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900">Selected Coordinates:</p>
                <p className="text-lg font-mono text-green-800">{selectedCoords}</p>
              </div>
              <Button
                onClick={() => {
                  const [lat, lng] = selectedCoords.split(',');
                  window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
                }}
                variant="outline"
                size="sm"
              >
                View on Map
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmLocation}
            disabled={!selectedCoords}
          >
            Confirm Location
          </Button>
        </div>

        {/* Additional Help */}
        <div className="text-xs text-gray-500 text-center">
          <p>💡 Tip: You can also share location from Google Maps mobile app</p>
          <p>Open Maps → Long press location → Share → Copy coordinates</p>
        </div>
      </div>
    </Modal>
  );
};

export default MapPicker;